<template>
  <div class="single-activity-section">
    <!-- Single Activity Header -->
    <div class="single-activity-header">
      <h2 class="single-activity-title">Single Activity</h2>
      <div class="single-activity-actions">
        <ActionButton
          variant="outline"
          @click="$emit('addActivity')"
          class="add-activity-btn"
        >
          <template #icon-left>
            <PlusIcon width="24" height="24" />
          </template>
          <span>Add an Activity</span>
        </ActionButton>
        <ActionButton
          variant="primary"
          :disabled="!hasSingleActivities"
          @click="$emit('bundleActivities')"
          class="bundle-activities-btn"
        >
          <template #icon-left>
            <SyncIcon />
          </template>

          <span>Bundle into a Makercart</span>
        </ActionButton>
      </div>
    </div>

    <!-- Divider Line -->
    <div class="single-activity-divider"></div>

    <!-- Single Activities List -->
    <div class="single-activities-list">
      <div
        v-for="activity in singleActivities"
        :key="activity.makercart_id"
        class="single-activity-item"
      >
        <div class="activity-info">
          <!-- Desktop Menu -->
          <v-menu z-index="10000000" v-if="!isMobile">
            <template #activator="{ props: menuProps }">
              <button class="activity-icon-btn" v-bind="menuProps" @click.stop>
                <MoreHorizontalIcon />
              </button>
            </template>
            <v-list class="activity-menu-list">
              <!-- Move to another Cart -->
              <v-list-item
                class="activity-menu-item"
                @click="$emit('moveActivity', activity)"
              >
                <v-list-item-title>
                  <div class="activity-action-button">
                    <svg
                      width="20"
                      height="11"
                      viewBox="0 0 20 11"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M15 1.49756L19 5.49756M19 5.49756L15 9.49756M19 5.49756L1 5.49756"
                        stroke="#3B4648"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                    <span>Move to another Cart</span>
                  </div>
                </v-list-item-title>
              </v-list-item>

              <!-- Delete Activity -->
              <v-list-item
                class="activity-menu-item"
                @click="$emit('deleteActivity', activity)"
              >
                <v-list-item-title>
                  <div class="activity-action-button">
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M3 6H5H21"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M10 11V17"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M14 11V17"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                    <span>Delete Activity</span>
                  </div>
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>

          <!-- Mobile Bottom Sheet Trigger -->
          <button
            v-if="isMobile"
            class="activity-icon-btn"
            @click="openBottomSheet(activity)"
            @click.stop
          >
            <MoreHorizontalIcon />
          </button>

          <div class="activity-details">
            <h3 class="activity-name">
              {{ activity.title }}
            </h3>
            <div class="poster-info">
              <span class="poster-label"
                >Poster: {{ activity.includingPoster ? "Yes" : "No" }}</span
              >
            </div>
            <div class="activity-price">${{ formatPrice(activity.price) }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Mobile Bottom Sheet -->
    <v-bottom-sheet
      v-model="showBottomSheet"
      class="activity-bottom-sheet"
      v-if="isMobile"
    >
      <v-card class="activity-bottom-sheet-card">
        <div class="activity-bottom-sheet-content">
          <!-- Drag Handle -->
          <div class="activity-bottom-sheet-handle"></div>

          <!-- Move to another Cart -->
          <div
            class="activity-bottom-sheet-item"
            @click="handleBottomSheetAction('move')"
          >
            <div class="activity-action-button">
              <svg
                width="20"
                height="11"
                viewBox="0 0 20 11"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M15 1.49756L19 5.49756M19 5.49756L15 9.49756M19 5.49756L1 5.49756"
                  stroke="#3B4648"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              <span>Move to another Cart</span>
            </div>
          </div>

          <!-- Delete Activity -->
          <div
            class="activity-bottom-sheet-item"
            @click="handleBottomSheetAction('delete')"
          >
            <div class="activity-action-button">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M3 6H5H21"
                  stroke="#3B4648"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z"
                  stroke="#3B4648"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M10 11V17"
                  stroke="#3B4648"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M14 11V17"
                  stroke="#3B4648"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              <span>Delete Activity</span>
            </div>
          </div>
        </div>
      </v-card>
    </v-bottom-sheet>
  </div>
</template>

<script setup>
/* eslint-disable no-undef */
import { defineProps, defineEmits, ref, onMounted, onUnmounted } from "vue";
import ActionButton from "@/components/common/ActionButton.vue";
import { PlusIcon, MoreHorizontalIcon } from "@/components/icons";
import SyncIcon from "@/components/icons/SyncIcon.vue";
import { formatPrice } from "@/utils/index.js";

defineProps({
  singleActivities: {
    type: Array,
    default: () => [],
  },
  hasSingleActivities: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  "addActivity",
  "bundleActivities",
  "moveActivity",
  "deleteActivity",
]);

const isMobile = ref(false);
const showBottomSheet = ref(false);
const selectedActivity = ref(null);

const checkMobile = () => {
  isMobile.value = window.innerWidth <= 768;
};

const openBottomSheet = (activity) => {
  selectedActivity.value = activity;
  showBottomSheet.value = true;
};

const handleBottomSheetAction = (action) => {
  const activity = selectedActivity.value;
  showBottomSheet.value = false;

  switch (action) {
    case "move":
      emit("moveActivity", activity);
      break;
    case "delete":
      emit("deleteActivity", activity);
      break;
  }
  selectedActivity.value = null;
};

onMounted(() => {
  checkMobile();
  window.addEventListener("resize", checkMobile);
});

onUnmounted(() => {
  window.removeEventListener("resize", checkMobile);
});
</script>

<style scoped>
@import "@/assets/css/colors.css";

/* Single Activity Section */
.single-activity-section {
  background-color: #f2f3fb;
  border-radius: 16px;
  padding: 16px;
  box-shadow:
    0px 1px 2px 0px rgba(0, 0, 0, 0.3),
    0px 2px 6px 2px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  margin-top: 24px;
}

.single-activity-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.single-activity-title {
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-large);
  font-weight: 700;
  line-height: 28px;
  color: #3b4648;
  margin: 0;
}

.single-activity-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.single-activity-divider {
  width: 100%;
  height: 2px;
  border-top: 2px dashed transparent;
  border-image: repeating-linear-gradient(
      to right,
      #e5e7eb 0 20px,
      /* dash: 10px */ transparent 20px 40px /* gap: 10px */
    )
    1 round;
}

.single-activities-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
}

.single-activity-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 0 0 16px;
  width: 100%;
  min-height: 48px;
  gap: 32px;
}

.activity-info {
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 1;
}

.activity-icon-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border: none;
  background-color: transparent;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.activity-icon-btn:hover {
  background-color: rgba(0, 61, 106, 0.05);
}

.activity-details {
  display: flex;
  gap: 32px;
  flex: 1;
}

.activity-name {
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-large);
  font-weight: 400;
  line-height: 28px;
  color: #3b4648;
  margin: 0;
}

.poster-info {
  display: flex;
  align-items: center;
  gap: 4px;
}

.poster-label {
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-big);
  font-weight: 400;
  line-height: 24px;
  color: #98a2b3;
}

.poster-status {
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-medium);
  font-weight: 400;
  line-height: 24px;
  color: #3b4648;
}

.activity-price {
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-xl);
  font-weight: 700;
  line-height: 32px;
  color: #3b4648;
  text-align: right;
  align-self: center;
  margin-left: auto;
}

/* Activity Menu List Styles */
.activity-menu-list {
  background: white;
  border-radius: 12px !important;
  box-shadow:
    0px 6px 10px 4px rgba(0, 0, 0, 0.15),
    0px 2px 3px 0px rgba(0, 0, 0, 0.1);
  padding: 8px;
  min-width: 262px;
}

.activity-menu-item {
  margin-bottom: 2px;
  border-radius: 12px;
  transition: all 0.2s ease;
}

.activity-menu-item:last-child {
  margin-bottom: 0;
}

.activity-menu-item:hover {
  background-color: var(--white-hover);
}

.activity-menu-item.active {
  background-color: var(--primary-blue);
  color: white;
}

.activity-menu-item.active .activity-action-button {
  color: white;
}

.activity-menu-item.active .activity-action-button svg {
  stroke: white;
}

.activity-action-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 12px !important;
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-medium);
  line-height: 24px;
  letter-spacing: 0.024px;
  color: var(--black-body-text);
  width: 100%;
  height: 48px;
  box-sizing: border-box;
}

.activity-action-button:hover {
  background-color: transparent;
}

.activity-action-button svg {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  stroke: var(--black-body-text);
}

@media (max-width: 768px) {
  .single-activity-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }

  .single-activity-actions {
    width: 100%;
    flex-direction: column-reverse;
  }

  .add-activity-btn,
  .bundle-activities-btn {
    width: 100%;
  }

  .single-activity-item {
    padding: 0;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
  }

  .activity-details {
    gap: 8px;
    justify-content: space-between;
  }

  .activity-name {
    max-width: 100px;
    font-size: var(--fs-small);
    font-style: normal;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: 0.014px;
  }

  .activity-info {
    width: 100%;
  }

  .activity-price {
    font-size: var(--fs-small);
    font-style: normal;
    font-weight: 700;
    margin-left: 0;
  }
}

/* Mobile Bottom Sheet Styles */
.activity-bottom-sheet {
  z-index: 10000001;
}

.activity-bottom-sheet-card {
  border-radius: 28px 28px 0 0 !important;
  overflow: hidden;
  background-color: #fff;
}

.activity-bottom-sheet-handle {
  width: 40px;
  height: 4px;
  background-color: #d8dae5;
  border-radius: 2px;
  margin: 12px auto 0;
  flex-shrink: 0;
}

.activity-bottom-sheet-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 12px 16px 24px;
  max-height: 60vh;
  overflow-y: auto;
}

.activity-bottom-sheet-item {
  width: 100%;
  cursor: pointer;
}

.activity-bottom-sheet-item .activity-action-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  height: 48px;
  border: 1px solid var(--primary-blue);
  border-radius: 12px;
  background-color: #fff;
  transition: all 0.2s ease;
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-medium);
  line-height: 24px;
  letter-spacing: 0.024px;
  color: #3b4648;
  width: 100%;
  box-sizing: border-box;
}

.activity-bottom-sheet-item .activity-action-button:hover {
  background-color: rgba(0, 61, 106, 0.05);
}

.activity-bottom-sheet-item .activity-action-button svg {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  stroke: #3b4648;
}

.activity-bottom-sheet-item .activity-action-button span {
  flex: 1;
  text-align: left;
}

@media (max-width: 768px) {
  .activity-bottom-sheet-content {
    padding: 12px 16px 24px;
  }
}
</style>
